import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { fbm2 } from '../../engine/math/noise';
import { clamp, smoothstep } from '../../engine/math/easing';
import { Heightfield } from '../../engine/geo/heightfield';
import { Transform } from '../../engine/core/Transform';
import type { Color } from '../../engine/geo/mesh';
import { BIOMES, BiomeId, CORRIDORS, PALETTE, SPOTS, biomeAt, segDist } from './Layout';
import type { Ctx } from '../Ctx';

const H = BIOMES;

/** Flatten helper: blend toward `level` inside radius r with a soft edge. */
function stamp(h: number, x: number, z: number, cx: number, cz: number, r: number, level: number, edge = 6): number {
  const d = Math.hypot(x - cx, z - cz);
  if (d > r + edge) return h;
  const k = 1 - smoothstep(clamp((d - r) / edge, 0, 1));
  return h + (level - h) * k;
}

// ---- marsh geometry (islands & black channels are shared with the marsh builder) ----
export const MARSH_ISLANDS: { x: number; z: number; r: number }[] = [
  { x: -150, z: 0, r: 16 },   // entry shore
  { x: -196, z: 22, r: 13 },  // near island (plank)
  { x: -170, z: -30, r: 9 },
  { x: -232, z: -26, r: 14 }, // far island (gold key shrine)
  { x: -246, z: 18, r: 8 },
];
/** Black water channels: segments with half-width `w` and bank slope width `bank`. The neck is plank-wide. */
export const MARSH_CHANNELS: { a: Vec3; b: Vec3; w: number; bank: number }[] = [
  // one continuous line of black water from cliff to cliff, separating the far island (west) from the rest
  { a: new Vec3(-238, 0, -72), b: new Vec3(-222, 0, -42), w: 4.5, bank: 3 },
  { a: new Vec3(-222, 0, -42), b: new Vec3(-222, 0, -10), w: 3.5, bank: 2 },
  { a: new Vec3(-222, 0, -10), b: new Vec3(-222, 0, -7), w: 3, bank: 2 },
  { a: new Vec3(-222, 0, -7), b: new Vec3(-214, 0, -1), w: 1.2, bank: 0.6 },   // the neck: plank crossing
  { a: new Vec3(-214, 0, -1), b: new Vec3(-208, 0, 6), w: 3, bank: 2 },
  { a: new Vec3(-208, 0, 6), b: new Vec3(-230, 0, 76), w: 4.5, bank: 3 },
  // a dead-end pool west of the far island for texture
  { a: new Vec3(-268, 0, 44), b: new Vec3(-252, 0, 14), w: 3.5, bank: 3 },
];
export const MARSH_WATER_Y = 0;
/** Terrain below this is black water: sink and die. */
export const MARSH_DEADLY_Y = -1.5;

function marshHeight(x: number, z: number): number {
  let h = -0.55 + fbm2(x * 0.06, z * 0.06, 2, 31) * 0.25; // shallow water bed
  for (const i of MARSH_ISLANDS) h = stamp(h, x, z, i.x, i.z, i.r, 0.55 + fbm2(x * 0.1, z * 0.1, 2, 32) * 0.15, 5);
  for (const c of MARSH_CHANNELS) {
    const d = segDist(x, z, c.a, c.b);
    if (d < c.w + c.bank) {
      const k = 1 - smoothstep(clamp((d - c.w) / c.bank, 0, 1));
      h = h + (-2.6 - h) * k;
    }
  }
  return h;
}

function dunesHeight(x: number, z: number): number {
  const lx = x - H.dunes.center.x, lz = z - H.dunes.center.z;
  // sharp ridge lines: 1 - |noise| gives crests
  const n = fbm2(lx * 0.018 + 3, lz * 0.018, 3, 41);
  let h = (1 - Math.abs(n)) * 9 + fbm2(lx * 0.05, lz * 0.05, 2, 42) * 1.2;
  h += fbm2(lx * 0.006, lz * 0.006, 2, 43) * 5;
  h = stamp(h, x, z, SPOTS.obelisk.x, SPOTS.obelisk.z, 9, 2.5, 8);
  h = stamp(h, x, z, SPOTS.whiteKeyBuried.x, SPOTS.whiteKeyBuried.z, 5, 3.0, 6);
  // corridor mouth flat
  h = stamp(h, x, z, 0, -138, 10, 1.0, 10);
  return h;
}

function ironwoodHeight(x: number, z: number): number {
  const lx = x - H.ironwood.center.x, lz = z - H.ironwood.center.z;
  let h = fbm2(lx * 0.03, lz * 0.03, 2, 51) * 1.4;
  h = stamp(h, x, z, SPOTS.whiteKeep.x, SPOTS.whiteKeep.z, 16, 0.4, 6);
  h = stamp(h, x, z, SPOTS.well.x, SPOTS.well.z, 5, 0.4, 4);
  // ravine (optional plank shortcut): a trench along one cell boundary of the maze (cells (1,3)/(1,4))
  const rd = segDist(x, z, new Vec3(165.5, 0, -5), new Vec3(174.5, 0, -5));
  if (rd < 1.3) h -= 6; else if (rd < 1.9) h -= 6 * (1 - (rd - 1.3) / 0.6);
  return h;
}

function hearthHeight(x: number, z: number): number {
  let h = fbm2(x * 0.025, z * 0.025, 3, 21) * 3.2 + fbm2(x * 0.007, z * 0.007, 2, 22) * 3;
  h = stamp(h, x, z, SPOTS.goldKeep.x, SPOTS.goldKeep.z, 20, 0, 8);
  h = stamp(h, x, z, SPOTS.stoneCircle.x, SPOTS.stoneCircle.z, 9, 0.6, 6);
  // stream: winding channel across the south-west
  const sd = streamDist(x, z);
  if (sd < 4) h -= (1 - smoothstep(clamp(sd / 4, 0, 1))) * 0.9;
  return h;
}
export function streamDist(x: number, z: number): number {
  // polyline from north-west to south-east through the meadow's west side
  const pts = [new Vec3(-58, 0, -40), new Vec3(-40, 0, -12), new Vec3(-44, 0, 14), new Vec3(-28, 0, 34), new Vec3(-30, 0, 60)];
  let best = Infinity;
  for (let i = 0; i < pts.length - 1; i++) best = Math.min(best, segDist(x, z, pts[i], pts[i + 1]));
  return best;
}

function cisternHeight(x: number, z: number): number {
  // ankle-deep stone floor; the chasm pit crosses corridor A, the west arm floods and deepens
  let h = -0.12 + fbm2(x * 0.3, z * 0.3, 1, 81) * 0.04;
  const cd = segDist(x, z, new Vec3(SPOTS.cisternChasm.x, 0, 550), new Vec3(SPOTS.cisternChasm.x, 0, 570));
  if (cd < 1.25) h = -9;
  else if (cd < 1.75) h = -9 + (cd - 1.25) / 0.5 * 8.88;
  if (x < -34 && z > 596) {
    const t = clamp((z - 596) / 16, 0, 1);
    h = Math.min(h, -0.5 - t * 2.2);
  }
  return h;
}

const localHeight: Record<BiomeId, (x: number, z: number) => number> = {
  hearth: hearthHeight, marsh: marshHeight, dunes: dunesHeight, ironwood: ironwoodHeight, cistern: cisternHeight,
};

/** Cliff ring around each surface biome, opened at the corridors. */
function cliff(x: number, z: number, id: BiomeId): number {
  if (id === 'cistern') return 0;
  const b = H[id];
  const d = Math.hypot(x - b.center.x, z - b.center.z) - b.radius;
  if (d <= 0) return 0;
  let rise = Math.min(16, d * 1.6 + d * d * 0.05) + fbm2(x * 0.08, z * 0.08, 2, 61) * 1.5 * clamp(d / 4, 0, 1);
  for (const c of CORRIDORS) {
    if (c.from !== id && c.to !== id) continue;
    const cd = segDist(x, z, c.a, c.b) - c.width / 2;
    if (cd < 6) rise *= smoothstep(clamp(cd / 6, 0, 1));
  }
  return rise;
}

/** The single global terrain function; every region samples it so seams line up. */
export function worldHeight(x: number, z: number): number {
  const id = biomeAt(x, z);
  return localHeight[id](x, z) + cliff(x, z, id);
}

function colorFor(id: BiomeId, x: number, y: number, z: number, slope: number): Color {
  const p = PALETTE[id];
  const n = fbm2(x * 0.15, z * 0.15, 2, 71);
  switch (id) {
    case 'hearth': {
      if (slope > 0.75) return p[2];
      if (streamDist(x, z) < 2.2) return [0.55, 0.5, 0.35];
      return n > 0.15 ? p[1] : p[0];
    }
    case 'marsh': {
      if (y < -1.4) return p[2];
      if (y < 0) return [0.18, 0.28, 0.24];
      return n > 0.1 ? p[1] : p[0];
    }
    case 'dunes': {
      if (slope > 0.9) return p[2];
      return slope > 0.5 ? p[1] : p[0];
    }
    case 'ironwood': return n > 0.2 ? p[1] : p[0];
    case 'cistern': return y < -3 ? [0.02, 0.02, 0.03] : y < -1 ? p[2] : n > 0 ? p[1] : p[0];
  }
}

/** Build one heightfield + mesh per biome region, plus a hard boundary at the world edge. */
export function buildTerrain(ctx: Ctx): Record<BiomeId, Heightfield> {
  const out = {} as Record<BiomeId, Heightfield>;
  for (const b of Object.values(BIOMES)) {
    const w = b.region.max.x - b.region.min.x, d = b.region.max.z - b.region.min.z;
    const cells = b.id === 'cistern' ? 200 : 100; // 1 m cells underground so the chasm fits under a 4 m plank
    const hf = new Heightfield(w, d, cells, cells, worldHeight, b.region.min.x, b.region.min.z);
    ctx.statics.addHeightfield(hf);
    const mesh = hf.toMesh((x, y, z, s) => colorFor(b.id, x, y, z, s));
    ctx.spawn('terrain.' + b.id, new Transform(), mesh, { noCull: true });
    out[b.id] = hf;
    // invisible boundary belt on every edge that is not a seam with the hub (seams get gapped walls below)
    const m = b.region.min, M = b.region.max;
    const t = 4;
    const seamW = b.id === 'hearth' || b.id === 'ironwood';
    const seamE = b.id === 'hearth' || b.id === 'marsh';
    const seamN = b.id === 'hearth';
    const seamS = b.id === 'dunes';
    if (!seamW) ctx.statics.addBox(AABB.fromMinMax(m.x - t, -100, m.z, m.x, 100, M.z), 'bound');
    if (!seamE) ctx.statics.addBox(AABB.fromMinMax(M.x, -100, m.z, M.x + t, 100, M.z), 'bound');
    if (!seamN) ctx.statics.addBox(AABB.fromMinMax(m.x, -100, m.z - t, M.x, 100, m.z), 'bound');
    if (!seamS) ctx.statics.addBox(AABB.fromMinMax(m.x, -100, M.z, M.x, 100, M.z + t), 'bound');
  }
  // seams between hub and spokes are open only through the corridor; block the rest of the seam line
  const seam = (x0: number, z0: number, x1: number, z1: number) => ctx.statics.addBox(AABB.fromMinMax(Math.min(x0, x1), -100, Math.min(z0, z1), Math.max(x0, x1), 100, Math.max(z0, z1)), 'seam');
  // hearth|marsh seam at x=-100, corridor z in [-4.5, 4.5]
  seam(-101, -100, -99, -6); seam(-101, 6, -99, 100);
  seam(99, -100, 101, -6); seam(99, 6, 101, 100);
  seam(-100, -101, -6, -99); seam(6, -101, 100, -99);
  // marsh outer west, dunes north, ironwood east are closed by the box above
  return out;
}
